import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { FileText, Map as MapIcon } from "lucide-react";
import useApiClient from "@/src/hooks/useApiClient";
import { AttachedFile, getMpzpFileUrl } from "@/src/api/Tenant";

type ParcelZone = {
  oznaczenie: string;
  symbol: string;
  nazwa: string;
  paragrafy: number[];
  strony: Array<number | null>;
  areaM2: number;
  percent: number;
};

type ParcelZonesResponse = {
  parcelAreaM2: number;
  coveredAreaM2: number;
  coveredPercent: number;
  zones: ParcelZone[];
  sheets: Array<{ label: string; fileId?: string }>;
};

/** Czy procedura ma wgrane tereny MPZP (wektor) — jedno zapytanie na stronę. */
export const useMpzpZonesStatus = (tenant: string, procedureId: string | undefined) => {
  const { publicApiClient } = useApiClient();
  return useQuery<{ hasZones: boolean }, AxiosError>({
    queryKey: ["mpzp-zones-status", tenant, procedureId],
    queryFn: async () =>
      (await publicApiClient.get(`/tenant/${tenant}/plan/${encodeURIComponent(procedureId!)}/mpzp-zones/status`)).data,
    enabled: !!procedureId,
    staleTime: 10 * 60_000,
    retry: false,
  });
};

/** Geometria działki z ULDK (useUldk zwraca TABLICĘ geometrii WGS84 z
 *  dodatkowymi polami center/bbox) → czysty GeoJSON Polygon/MultiPolygon. */
function toPolygonGeometry(geometry: any): { type: "Polygon" | "MultiPolygon"; coordinates: any } | null {
  const parts: any[] = (Array.isArray(geometry) ? geometry : [geometry]).filter(
    (g) => g && (g.type === "Polygon" || g.type === "MultiPolygon") && Array.isArray(g.coordinates),
  );
  if (parts.length === 0) return null;
  if (parts.length === 1) return { type: parts[0].type, coordinates: parts[0].coordinates };
  const polys = parts.flatMap((g) => (g.type === "Polygon" ? [g.coordinates] : g.coordinates));
  return { type: "MultiPolygon", coordinates: polys };
}

const fmtNum = (n: number, digits = 0) =>
  n.toLocaleString("pl-PL", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/**
 * Karta pod wybraną działką: przeznaczenia terenu z projektu planu
 * (przecięcie z wektorem terenów MPZP) — oznaczenie, nazwa z legendy,
 * udział %, odnośnik do paragrafu w tekście planu (PDF otwierany na
 * właściwej stronie) oraz arkusz rysunku planu do pobrania.
 */
export default function ParcelZonesInfo({
  tenant,
  procedureId,
  parcelId,
  geometry,
  hasPlanText,
  files,
  sourceLabel,
}: {
  tenant: string;
  procedureId: string;
  parcelId: string;
  geometry: any;
  hasPlanText: boolean;
  files?: AttachedFile[];
  /** Nazwa źródła terenów widoczna dla mieszkańca, np. "Projekt MPZP Gminy
   *  Łącko" (payload.mpzpZonesLabel). */
  sourceLabel?: string;
}) {
  const { publicApiClient } = useApiClient();
  const cleanGeometry = toPolygonGeometry(geometry);
  const q = useQuery<ParcelZonesResponse, AxiosError>({
    queryKey: ["parcel-zones", tenant, procedureId, parcelId],
    queryFn: async () =>
      (
        await publicApiClient.post(`/tenant/${tenant}/plan/${encodeURIComponent(procedureId)}/parcel-zones`, {
          geometry: cleanGeometry,
          srid: 4326,
        })
      ).data,
    enabled: !!cleanGeometry,
    staleTime: 30 * 60_000,
    retry: 1,
  });

  const planTextUrl = hasPlanText ? getMpzpFileUrl(tenant, procedureId, "plan-text") : null;

  if (!cleanGeometry) return null;
  if (q.isLoading) {
    return <div className="mt-2 text-xs text-gray-500">Sprawdzam przeznaczenie terenu w projekcie planu…</div>;
  }
  if (q.isError || !q.data) {
    return <div className="mt-2 text-xs text-gray-400">Nie udało się pobrać przeznaczenia terenu.</div>;
  }
  const d = q.data;
  const outside = Math.max(0, 100 - d.coveredPercent);

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 mb-1.5">
        <span className="font-semibold text-gray-800">Przeznaczenie w projekcie planu</span>
        <span className="text-xs text-gray-500 shrink-0">pow. działki {fmtNum(d.parcelAreaM2)} m²</span>
      </div>
      {sourceLabel && <div className="-mt-1 mb-1.5 text-xs text-gray-600">wg: {sourceLabel}</div>}
      {d.zones.length === 0 ? (
        <p className="text-xs text-gray-600">Działka leży poza obszarem objętym projektem planu.</p>
      ) : (
        <ul className="space-y-2">
          {d.zones.map((z) => (
            <li key={z.oznaczenie} className="border-l-4 border-blue-300 pl-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <span className="font-mono font-semibold text-blue-800 whitespace-nowrap">{z.oznaczenie}</span>
                <span className="text-xs text-gray-700 shrink-0">
                  <strong>{fmtNum(z.percent, 1)}%</strong> ({fmtNum(z.areaM2)} m²)
                </span>
              </div>
              {z.nazwa && <div className="text-xs text-gray-700 leading-snug">{z.nazwa}</div>}
              {z.paragrafy.length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                  {z.paragrafy.map((p, i) => {
                    const page = z.strony?.[i];
                    return planTextUrl && page ? (
                      <a
                        key={p}
                        href={`${planTextUrl}#page=${page}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-700 underline hover:text-blue-900"
                        title={`Ustalenia szczegółowe — tekst planu, strona ${page}`}>
                        §{p} (str. {page})
                      </a>
                    ) : (
                      <span key={p} className="text-xs text-gray-500">
                        §{p}
                      </span>
                    );
                  })}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {d.zones.length > 0 && outside >= 0.5 && (
        <p className="mt-1.5 text-xs text-gray-500">Poza obszarem planu: {fmtNum(outside, 1)}% działki.</p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {planTextUrl && (
          <a
            href={planTextUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">
            <FileText className="h-3.5 w-3.5" /> Tekst planu
          </a>
        )}
        {d.sheets.map((s) => {
          const f = s.fileId ? files?.find((x) => x.id === s.fileId) : undefined;
          return f ? (
            <a
              key={s.label}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
              title={`Rysunek planu — ${s.label} (${f.filename})`}>
              <MapIcon className="h-3.5 w-3.5" /> Rysunek: {s.label}
            </a>
          ) : (
            <span key={s.label} className="inline-flex items-center gap-1 text-xs px-2 py-1 text-gray-600">
              <MapIcon className="h-3.5 w-3.5" /> {s.label}
            </span>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-gray-400">
        Zestawienie orientacyjne — wyliczone z warstwy terenów {sourceLabel ? `„${sourceLabel}”` : "projektu planu"} i granic
        działki z ewidencji. Wiążący jest rysunek i tekst planu.
      </p>
    </div>
  );
}
